/**
 * Taking a delivery in.
 *
 * WHY THIS SCREEN EXISTS
 * ----------------------
 * The system could already report what was expiring and refuse anything
 * already out of date — and there was no way to enter an expiry date, so the
 * report had nothing to report on. This is where the date comes from.
 *
 * WHAT THE PERSON USING IT IS DOING
 * ---------------------------------
 * Standing at a door with a lorry outside, counting boxes. So the screen
 * opens with what was ordered already filled in, because most deliveries
 * arrive complete and typing the same number back is how a wrong one gets
 * typed. What differs is what they change.
 *
 * WHY REJECTING IS ITS OWN COLUMN
 * -------------------------------
 * A delivery that is short and a delivery that is damaged are different
 * conversations with the supplier, and the invoice is settled differently.
 * Recording ten received when twelve arrived and two were rotten loses the
 * fact that two were rotten.
 */
import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { formatPKR, today } from '@foodrun/shared';

import { api } from '../../lib/api.js';
import { Spinner } from '../../components/ui/Spinner.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { ScanBox } from './ScanBox.jsx';

/** A date shifted by days, as YYYY-MM-DD. */
function shift(day, days) {
  const date = new Date(`${day}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function ReceivePanel({ poId, onClose, onReceived }) {
  const detail = useQuery({
    queryKey: ['purchasing', 'order', poId],
    queryFn: () => api.get(`/purchasing/orders/${poId}`),
  });

  const [lines, setLines] = useState(null);
  const [note, setNote] = useState('');
  /** The line a scan just landed on, so the eye can find it. */
  const [scanned, setScanned] = useState(null);

  // Filled from the order the first time it arrives, then left alone — the
  // person is editing it after that.
  if (detail.data && lines === null) {
    setLines(
      detail.data.lines.map((line) => ({
        ...line,
        accepted: String(line.outstanding),
        rejected: '',
        rejectionReason: '',
        /**
         * Suggested from the ingredient's shelf life where one is recorded.
         * None are today, so this is usually blank and the date is read off
         * the packet — which is what somebody at a delivery door does anyway.
         */
        expiryDate: line.shelf_life_days ? shift(today(), line.shelf_life_days) : '',
        batchNumber: '',
      }))
    );
  }

  const setLine = (id, field, value) =>
    setLines((was) => was.map((l) => (l.id === id ? { ...l, [field]: value } : l)));

  const receive = useMutation({
    mutationFn: () =>
      api.post('/purchasing/grn', {
        poId,
        branchId: detail.data.order.branch_id,
        warehouseId: detail.data.order.warehouse_id ?? undefined,
        notes: note.trim() || undefined,
        items: lines
          .filter((l) => Number(l.accepted) > 0 || Number(l.rejected) > 0)
          .map((l) => ({
            ingredientId: l.ingredient_id,
            poLineId: l.id,
            unitId: l.unit_id,
            orderedQuantity: l.quantity,
            receivedQuantity: (Number(l.accepted) || 0) + (Number(l.rejected) || 0),
            acceptedQuantity: Number(l.accepted) || 0,
            rejectedQuantity: Number(l.rejected) || 0,
            rejectionReason: l.rejectionReason || undefined,
            unitCost: l.unit_price,
            expiryDate: l.expiryDate || undefined,
            batchNumber: l.batchNumber || undefined,
          })),
      }),
    onSuccess: onReceived,
  });

  if (detail.isLoading || lines === null) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Spinner label="Loading the order" />
      </div>
    );
  }

  if (detail.isError) {
    return (
      <p className="py-12 text-center text-sm text-ink-2">
        Could not load that order. Check the branch computer is running.
      </p>
    );
  }

  const order = detail.data.order;
  const taking = lines.filter((l) => Number(l.accepted) > 0 || Number(l.rejected) > 0);
  const value = lines.reduce((sum, l) => sum + (Number(l.accepted) || 0) * (l.unit_price || 0), 0);

  /**
   * Perishables about to be taken in with no date on them.
   *
   * Said before the delivery is accepted rather than after. Once it is in,
   * nothing can warn about it — stock with no date never appears on the
   * expiring report, and an empty report reads as "nothing is expiring".
   */
  const undated = taking.filter((l) => l.is_perishable && !l.expiryDate && Number(l.accepted) > 0);

  const field =
    'h-10 rounded-lg border border-rule bg-surface-3 px-2 text-sm text-ink focus:border-brand-400 focus:outline-none';

  return (
    <div className="rounded-xl border border-rule bg-surface p-5">
      <header className="mb-4 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <h2 className="font-medium text-ink">Take in {order.po_number}</h2>
          <p className="mt-0.5 text-sm text-ink-3">From {order.supplier_name}</p>
        </div>
        <span className="text-sm tabular-nums text-ink-2">{formatPKR(value)}</span>
      </header>

      {/*
        Where somebody actually stands with a scanner. Scanning a line marks
        it, so a delivery of twelve boxes is twelve scans rather than twelve
        readings of a list.
      */}
      <ScanBox
        lines={lines}
        onMatch={(ingredient) => setScanned(ingredient.id)}
        onAttached={(ingredient) => setScanned(ingredient.id)}
      />

      <div className="mt-3 space-y-3">
        {lines.map((line) => (
          <div
            key={line.id}
            className={`rounded-lg border p-3 ${
              scanned === line.ingredient_id ? 'border-brand-400 bg-brand-400/5' : 'border-rule'
            }`}
          >
            <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
              <span className="text-sm text-ink">
                {line.ingredient_name}
                {line.is_perishable && (
                  <span className="ml-2 rounded border border-warn px-1.5 py-0.5 text-xs text-warn">
                    perishable
                  </span>
                )}
              </span>
              <span className="text-xs text-ink-3">
                {line.outstanding} {line.unit_abbr} still owed
                {line.received_quantity > 0 && ` · ${line.received_quantity} already in`}
              </span>
            </div>

            <div className="grid gap-2 sm:grid-cols-4">
              <label>
                <span className="mb-1 block text-xs text-ink-3">Good</span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={line.accepted}
                  onChange={(e) => setLine(line.id, 'accepted', e.target.value)}
                  aria-label={`Good quantity of ${line.ingredient_name}`}
                  className={`${field} w-full text-right tabular-nums`}
                />
              </label>

              <label>
                <span className="mb-1 block text-xs text-ink-3">Rejected</span>
                <input
                  type="number"
                  min="0"
                  step="0.001"
                  value={line.rejected}
                  onChange={(e) => setLine(line.id, 'rejected', e.target.value)}
                  aria-label={`Rejected quantity of ${line.ingredient_name}`}
                  className={`${field} w-full text-right tabular-nums`}
                />
              </label>

              <label>
                <span className="mb-1 block text-xs text-ink-3">
                  Use by {line.is_perishable && <span className="text-warn">·</span>}
                </span>
                <input
                  type="date"
                  value={line.expiryDate}
                  onChange={(e) => setLine(line.id, 'expiryDate', e.target.value)}
                  aria-label={`Use by date for ${line.ingredient_name}`}
                  className={`${field} w-full`}
                />
              </label>

              <label>
                <span className="mb-1 block text-xs text-ink-3">Batch</span>
                <input
                  value={line.batchNumber}
                  onChange={(e) => setLine(line.id, 'batchNumber', e.target.value)}
                  aria-label={`Batch number for ${line.ingredient_name}`}
                  className={`${field} w-full`}
                />
              </label>
            </div>

            {/* Asked only when something has been rejected, because the
                answer is what the supplier is told. */}
            {Number(line.rejected) > 0 && (
              <input
                value={line.rejectionReason}
                onChange={(e) => setLine(line.id, 'rejectionReason', e.target.value)}
                placeholder="What was wrong with it"
                aria-label={`Why ${line.ingredient_name} was rejected`}
                className={`${field} mt-2 w-full`}
              />
            )}

            {line.expiryDate && line.expiryDate < today() && (
              <p className="mt-2 text-sm text-bad">
                That date has already passed. This cannot be taken in.
              </p>
            )}
          </div>
        ))}
      </div>

      <label className="mt-3 block">
        <span className="mb-1 block text-xs text-ink-3">Anything to note</span>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          aria-label="Note"
          className={`${field} w-full`}
        />
      </label>

      {undated.length > 0 && (
        <p className="mt-3 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
          {undated.map((l) => l.ingredient_name).join(', ')}{' '}
          {undated.length === 1 ? 'is perishable and has' : 'are perishable and have'} no use-by
          date. Nothing will warn about {undated.length === 1 ? 'it' : 'them'} later.
        </p>
      )}

      {receive.isError && (
        <div className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
          {receive.error?.details?.length ? (
            <ul className="space-y-0.5">
              {receive.error.details.map((problem) => (
                <li key={problem}>{problem}</li>
              ))}
            </ul>
          ) : (
            (receive.error?.message ?? 'That did not work.')
          )}
        </div>
      )}

      <div className="mt-4 flex flex-wrap items-center justify-end gap-2">
        <span className="mr-auto text-sm text-ink-3">
          {taking.length} of {lines.length} lines
        </span>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          onClick={() => receive.mutate()}
          disabled={taking.length === 0 || receive.isPending}
        >
          {receive.isPending ? 'Taking in' : 'Take it in'}
        </Button>
      </div>
    </div>
  );
}
